import { Check } from '@timelyapp/icons/solid-icons';
import type { ButtonSize } from '@timelyapp/react';
import classNames from 'classnames';
import { EMPTY_ARRAY } from 'constants/defaults';
import React from 'react';
import { isContrastReadable, prefixHexColor } from 'utils/colors';
import styles from './ColorPicker.module.scss';

interface ColorPickerProps {
  /** Class name for the container. */
  className?: string;
  /** Hex value of the currently selected color. */
  color?: string;
  /** Array of default color options. */
  defaultColors?: ReadonlyArray<string>;
  /** Max width for the container. */
  maxWidth?: number;
  /** Callback to update the currently selected color. */
  setColor: (color: string) => void;
  /** Size of the color circles. */
  size?: ButtonSize;
}

const ColorPicker = React.memo<ColorPickerProps>(
  ({ className, color, defaultColors = EMPTY_ARRAY, maxWidth, setColor, size = 'medium' }) => {
    const regex = new RegExp(`^#?(${color})$`, 'i');

    return (
      <ul
        className={classNames(className, styles.colors, styles[`size-${size}`])}
        style={{ maxWidth }}
      >
        {defaultColors.map(color => {
          const hexColor = prefixHexColor(color);
          const isSelected = regex.test(hexColor);
          return (
            <li
              key={hexColor}
              className={classNames(styles.color, {
                [styles.selected]: isSelected,
                [styles.light]: !isContrastReadable('#fff', hexColor),
              })}
              style={{
                backgroundColor: hexColor,
                outlineColor: hexColor,
              }}
              onClick={() => setColor(hexColor)}
            >
              {isSelected && <Check />}
            </li>
          );
        })}
      </ul>
    );
  }
);

ColorPicker.displayName = 'ColorPicker';
export default ColorPicker;
